import React from 'react';
import { ExternalLink } from 'lucide-react';

const screenshot  = '/images/games/poppy-cat-screenshot.png';
const icon        = '/images/games/poppy-cat-icon.png';
const pbsLogo     = '/images/logos/pbs-sprout-logo.png';
const coolabiLogo = '/images/logos/coolabi-logo.png';

const screens = [
  { src: '/images/games/poppy-cat-checkout.png', label: 'Checkout' },
  { src: '/images/games/poppy-cat-sandwich.png', label: 'Bakery' },
  { src: '/images/games/poppy-cat-picnic.png',   label: 'Picnic' },
  { src: '/images/games/poppy-cat-puzzle.png',   label: 'Puzzle' },
];

const AppStoreIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GooglePlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.18 23.76c.3.17.64.22.99.14l12.47-7.18-2.79-2.8-10.67 9.84zm-1.61-21c-.1.27-.15.56-.15.87v18.74c0 .31.05.6.15.87l.1.1 10.49-10.49v-.25L1.57 2.66l-.1.1zm20.04 9.28l-2.99-1.73-3.14 3.14 3.14 3.14 3-.73c.86-.49.86-1.29 0-1.82zM4.17.24L16.64 7.42l-2.79 2.79L3.18.37c.35-.08.69-.03.99.14v-.27z"/>
  </svg>
);

export function PoppyCat() {
  return (
    <section className="py-16 relative overflow-hidden bg-slate-900">
      {/* Neon tron grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `
            linear-gradient(rgba(34,211,238,0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,211,238,0.12) 1px, transparent 1px),
            linear-gradient(rgba(168,85,247,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(168,85,247,0.06) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px, 60px 60px, 20px 20px, 20px 20px',
        }} />
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-cyan-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-orange-400/5 rounded-full blur-3xl" />
      </div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section label */}
        <div className="flex items-center justify-center space-x-3 mb-10">
          <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse">[</span>
          <span className="text-white tracking-wider uppercase text-xl font-cyberpunk bg-gradient-to-r from-orange-300 to-cyan-300 bg-clip-text text-transparent cyberpunk-glow drop-shadow-lg">
            Previously Published
          </span>
          <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse">]</span>
        </div>

        {/* Card */}
        <div className="max-w-6xl mx-auto bg-gradient-to-br from-slate-800/60 to-slate-700/30 backdrop-blur-sm rounded-2xl border border-cyan-500/20 overflow-hidden hover:border-cyan-400/40 transition-all duration-500 shadow-2xl shadow-cyan-400/5">
          <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />

          {/* Top row — screenshot + info */}
          <div className="flex flex-col lg:flex-row" style={{ minHeight: '380px' }}>

            {/* Screenshot */}
            <div className="lg:w-1/2 relative overflow-hidden aspect-[4/3] lg:aspect-auto">
              <img
                src={screenshot}
                alt="Poppy Cat Goes to the Market"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>

            {/* Info */}
            <div className="lg:w-1/2 flex flex-col justify-between p-6 md:p-10 border-t lg:border-t-0 lg:border-l border-cyan-500/10">

              {/* Icon + Title */}
              <div>
                <div className="flex items-center space-x-5 mb-5">
                  <img
                    src={icon}
                    alt="Poppy Cat Icon"
                    className="w-24 h-24 rounded-3xl shadow-lg shadow-black/60 border border-cyan-500/20 flex-shrink-0"
                  />
                  <div>
                    <h2 className="text-white font-black text-3xl leading-tight">Poppy Cat</h2>
                    <h3 className="text-cyan-300 font-cyberpunk text-sm tracking-wide">Goes to the Market</h3>
                  </div>
                </div>

                {/* Store badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="flex items-center space-x-1 px-2 py-1 bg-slate-700/50 rounded border border-cyan-500/20 text-xs text-gray-400 font-mono">
                    <AppStoreIcon className="h-3 w-3 text-cyan-400" /><span>App Store</span>
                  </span>
                  <span className="flex items-center space-x-1 px-2 py-1 bg-slate-700/50 rounded border border-cyan-500/20 text-xs text-gray-400 font-mono">
                    <GooglePlayIcon className="h-3 w-3 text-cyan-400" /><span>Google Play</span>
                  </span>
                  <span className="px-2 py-1 bg-slate-700/50 rounded border border-cyan-500/20 text-xs text-gray-400 font-mono">Amazon</span>
                </div>

                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  Go shopping with Poppy Cat and friends to learn pre-K Spanish with this adorable and entertaining app from Cosmic Forces.
                </p>
              </div>

              {/* Partner logos + link */}
              <div>
                <p className="text-gray-500 text-xs font-mono uppercase tracking-widest mb-3">In partnership with</p>
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-white/90 rounded-xl border border-cyan-500/20">
                    <img src={pbsLogo} alt="PBS Kids Sprout" className="h-10 w-auto" />
                  </div>
                  <div className="p-3 bg-white/90 rounded-xl border border-cyan-500/20">
                    <img src={coolabiLogo} alt="Coolabi" className="h-10 w-auto" />
                  </div>
                </div>
                <a
                  href="http://www.poppycat.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors duration-300 text-sm font-mono group"
                >
                  <span>poppycat.com</span>
                  <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom strip — 4 gameplay screenshots */}
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-cyan-500/20">
            {screens.map((s, i) => (
              <div
                key={i}
                className="relative overflow-hidden border-r border-cyan-500/10 last:border-r-0 even:border-r-0 lg:even:border-r lg:last:border-r-0 border-b lg:border-b-0 group"
                style={{ aspectRatio: '16/9' }}
              >
                <img
                  src={s.src}
                  alt={s.label}
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 py-2 px-3 bg-gradient-to-t from-slate-900/80 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-cyan-300 font-mono text-xs tracking-widest uppercase">{s.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
        </div>
      </div>
    </section>
  );
}
